import { CompactTileButton } from '@/components/buttons/compact-tile/component';
import { TileButton } from '@/components/buttons/tile/component';
import { ContentPanel } from '@/components/content-panel';
import { CustomContentSection } from '@/components/custom-content-section';
import { DropPointsMap } from '@/components/drop-locations-map';
import { Logo } from '@/components/logo';
import { PrizeAlert } from '@/components/prize-alert';
import { CHECK_PRIZE_STATUS, CheckPrizeStatusQuery } from '@/components/prize-alert/graphql';
import { RenewalAlert } from '@/components/renewal-alert';
import { useLayoutContext } from '@/contexts';
import { PrimaryLayout } from '@/layouts/primary';
import { safeAreaTop } from '@/utils';
import { useQuery } from '@apollo/client';
import { Box, Flex, Grid, Space, Text, Transition } from '@mantine/core';
import {
	IconBuildingFactory,
	IconLayoutGridAdd,
	IconMapPin,
	IconShoppingCart,
	IconTrophy,
	IconZoomQuestion,
} from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
	ContentSectionLocation,
	ContentSectionPlacement,
	RoleName,
} from '../../../../backend/src/types.generated';

import { BilbyJump } from '@/components/bilby';
import { useCurrentUser } from '@/hooks/use-current-user';
import { NewsAndArticles } from './news-and-articles';
import { ScanStats } from './scan-stats';
import styles from './styles.module.css';
import { User } from './user';

export const HomePage = () => {
	const navigate = useNavigate();
	const { isDesktop } = useLayoutContext();
	const { data: prizeStatus } = useQuery<CheckPrizeStatusQuery>(CHECK_PRIZE_STATUS);
	const [dropPointsMapOpen, setDropPointsMapOpen] = useState(false);
	const goToScanBag = () => navigate({ to: '/scan' });
	const goToRewards = () => navigate({ to: '/rewards' });
	const goToShop = () => navigate({ to: '/shop' });
	const goToHelp = () => navigate({ to: '/help' });
	const notchPadding = safeAreaTop();
	const { currentUser } = useCurrentUser();
	const isFacilityUser = useMemo(() => {
		return !!(
			currentUser?.roles?.some((role) => role.name === RoleName.Facility) &&
			currentUser?.facilities?.length &&
			currentUser?.facilities?.length > 0
		);
	}, [currentUser]);

	const showPrizeAlert = useMemo(() => {
		if (!prizeStatus?.checkPrizeStatus) return false;
		if (prizeStatus.checkPrizeStatus.length > 0) return true;
		return false;
	}, [prizeStatus?.checkPrizeStatus]);

	return (
		<>
			<PrimaryLayout context="home">
				<Flex className={styles.header} h={279 + notchPadding} direction="column">
					<Flex align="center" justify="center" direction="column" h="100%" w="100%">
						<Space h={isDesktop ? 0 : notchPadding} />
						{isDesktop ? (
							<Flex>
								<Text className={styles.welcomeText}>Welcome to Curby</Text>
								<BilbyJump className={styles.bilby} />
							</Flex>
						) : (
							<Logo width="50%" height="40%" />
						)}
					</Flex>
					<Box className={styles.bottom} />
				</Flex>
				<ContentPanel noPad>
					<CustomContentSection
						location={ContentSectionLocation.Home}
						placement={ContentSectionPlacement.Top}
					/>
					<Flex justify="center" align="center" direction="column" gap="md" py="0" pb="lg">
						<Grid>
							<Transition mounted={showPrizeAlert} transition="fade" duration={1000}>
								{(style) => (
									<Grid.Col style={style} span={12}>
										<PrizeAlert />
									</Grid.Col>
								)}
							</Transition>
							<Grid.Col span={12}>
								<RenewalAlert />
							</Grid.Col>
							<Grid.Col span={12}>
								<User />
							</Grid.Col>
							<Grid.Col span={6}>
								<TileButton icon={IconLayoutGridAdd} text="Scan a QR Code" onClick={goToScanBag} />
							</Grid.Col>
							<Grid.Col span={6}>
								<TileButton icon={IconShoppingCart} text="Order" onClick={goToShop} />
							</Grid.Col>
							<Grid.Col span={6}>
								<TileButton icon={IconTrophy} text="Rewards" onClick={goToRewards} />
							</Grid.Col>
							<Grid.Col span={6}>
								<TileButton icon={IconZoomQuestion} text="Get Help" onClick={goToHelp} />
							</Grid.Col>
							<Grid.Col span={12}>
								<CompactTileButton
									icon={IconMapPin}
									text="Show Drop Points"
									onClick={() => setDropPointsMapOpen(!dropPointsMapOpen)}
								/>
							</Grid.Col>
							{isFacilityUser && (
								<Grid.Col span={12}>
									<CompactTileButton
										icon={IconBuildingFactory}
										text={`Switch to Facility`}
										onClick={() =>
											navigate({
												to: '/facility/home',
											})
										}
									/>
								</Grid.Col>
							)}
						</Grid>
					</Flex>

					<CustomContentSection
						location={ContentSectionLocation.Home}
						placement={ContentSectionPlacement.Middle}
					/>
					<ScanStats />
					<NewsAndArticles />
					<CustomContentSection
						location={ContentSectionLocation.Home}
						placement={ContentSectionPlacement.Bottom}
					/>
				</ContentPanel>
			</PrimaryLayout>
			<DropPointsMap open={dropPointsMapOpen} setOpen={setDropPointsMapOpen} />
		</>
	);
};
